import { h } from 'vue'
import { useI18n } from 'vue-i18n'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import { ViewCompactItemType, type ViewCompactFolder } from '@lib/types/files'
import { CoreFolderEntityType } from '@lib/types/folder'
import { FileContentTypeEnum } from '@openapi'
import dayjsInstance from 'dayjs'

export default function useFileDisplayHelpers() {
  const { t } = useI18n()

  function renderContentType(contentType: FileContentTypeEnum) {
    const labelsAndIcons = {
      [FileContentTypeEnum.AR]: { label: t('canvasUI.contentType.ar'), icon: 'cube' },
      [FileContentTypeEnum.FONT]: { label: t('canvasUI.contentType.font'), icon: 'font' },
      [FileContentTypeEnum.IMAGE]: { label: t('canvasUI.contentType.image'), icon: 'image' },
      [FileContentTypeEnum.PDF]: { label: t('canvasUI.contentType.pdf'), icon: 'file' },
      [FileContentTypeEnum.VIDEO]: { label: t('canvasUI.contentType.video'), icon: 'video' },
      [FileContentTypeEnum.WEB]: { label: t('canvasUI.contentType.web'), icon: 'globe' },
    }
    const entry = labelsAndIcons[contentType] || { label: t('canvasUI.contentType.unknown'), icon: 'question' }
    return h('span', [h(CIcon, { icon: entry.icon, class: 'mr-1' }), entry.label])
  }

  type FolderWithOptionalType = Omit<Partial<ViewCompactFolder>, 'type'> & {
    type?: ViewCompactItemType.Folder | ViewCompactItemType.SmartFolder
  }

  function getItemDescription(count: number, entity?: CoreFolderEntityType): string {
    if (!entity) {
      if (count === 1) return t('canvasUI.files.singleFile')
      else return t('canvasUI.files.multipleFiles', { count })
    }

    switch (entity) {
      case CoreFolderEntityType.Block:
        return count === 1 ? t('canvasUI.files.singleBlock') : t('canvasUI.files.multipleBlocks', { count })
      case CoreFolderEntityType.Canvas:
        return count === 1 ? t('canvasUI.files.singleCanvas') : t('canvasUI.files.multipleCanvases', { count })
      case CoreFolderEntityType.Section:
        return count === 1 ? t('canvasUI.files.singleSection') : t('canvasUI.files.multipleSections', { count })
      case CoreFolderEntityType.Template:
        return count === 1 ? t('canvasUI.files.singleTemplate') : t('canvasUI.files.multipleTemplates', { count })
      default:
        return count === 1 ? t('canvasUI.files.singleFile') : t('canvasUI.files.multipleFiles', { count })
    }
  }

  function getFolderDescription(
    item: FolderWithOptionalType & { available_files_count?: number; available_folders_count?: number },
    { entity, hideFilesCount }: { entity?: CoreFolderEntityType; hideFilesCount?: boolean } = {},
  ): string {
    const filesCount = item.available_files_count ?? item.files_count ?? 0
    const foldersCount = item.available_folders_count ?? item.folders_count ?? 0

    if (filesCount === 0 && foldersCount === 0) return t('canvasUI.files.emptyFolder')

    const itemsDescription = getItemDescription(filesCount, entity)

    if (item?.type === ViewCompactItemType.SmartFolder) {
      if (filesCount === 0) {
        return t('canvasUI.files.smartFolder')
      }

      if (hideFilesCount) {
        return t('canvasUI.files.smartFolder')
      } else {
        return t('canvasUI.files.smartFolderWithFiles', { count: filesCount })
      }
    }

    const foldersDescription =
      foldersCount === 1
        ? t('canvasUI.files.singleFolder')
        : t('canvasUI.files.multipleFolders', { count: foldersCount })

    if (hideFilesCount) {
      if (foldersCount === 0) {
        return t('canvasUI.common.folder')
      }

      return t('canvasUI.files.folderWithFolders', { foldersDescription })
    }

    if (filesCount === 0) {
      return t('canvasUI.files.folderWithFolders', { foldersDescription })
    } else if (foldersCount === 0) {
      return t('canvasUI.files.folderWithFiles', { itemsDescription })
    }

    return t('canvasUI.files.folderWithFilesAndFolders', {
      itemsDescription,
      foldersDescription,
    })
  }

  function isFileExpired(expiresAt: string) {
    return expiresAt && dayjsInstance(expiresAt).isBefore(new Date())
  }

  function isFileExpiring(expiresAt: string) {
    const expiring = expiresAt && dayjsInstance(expiresAt).isBefore(dayjsInstance().add(30, 'days'))
    return expiring ? dayjsInstance(expiresAt).fromNow() : null
  }

  return { getFolderDescription, renderContentType, isFileExpired, isFileExpiring }
}
